<template>
  <div class="page">
    <div class="page_contents">
      <div class="contents_div">
        <div class="page_header">
          자료실
        </div>
        <table class="styled-table">
          <thead>
            <tr>
              <th>구분</th>
              <th>제목</th>
              <th>등록일</th>
              <th>조회수</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(resourceBoard, index) in displayedResourceBoards" :key="index" @click="goDetail(resourceBoard)">
              <td>자료실</td>
              <td>{{ resourceBoard.제목 }}</td>
              <td>{{ convertDate(resourceBoard.등록일시) }}</td>
              <td>{{ resourceBoard.조회수 }}</td>
            </tr>
          </tbody>
        </table>
        <div class="pagination">
          <img class="pagination_left_arrow" @click="prevPage" src="@/assets/images/paging_arrow_left.png" />
          <button v-for="page in pages" :key="page" @click="goToPage(page)" :class="{ active: page === currentPage }">
            {{ page }}
          </button>
          <img class="pagination_right_arrow" @click="nextPage" src="@/assets/images/paging_arrow_right.png" />
        </div>
        <div class="search_div">
          <select>
            <option>제목</option>
          </select>
          <div class="search_wrap">
            <input v-model="searchTitle" type="text" @keyup.enter="getResourceBoards" />
            <img class="icon_search" src="../assets/images/icon-search.png" @click="getResourceBoards" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from "vue";
import { useRouter } from "vue-router";
import { ApiResponse, ResourceBoards } from "../common/types";
import resourceBoardAPI from "../api/resourceBoardAPI";
import { convertDate } from "../utils/utils";

const router = useRouter();

// 전체 데이터
const resourceBoardList = ref<ResourceBoards[]>([]);
const searchTitle = ref("");
// 현재 페이지에 보여줄 데이터
const currentPage = ref(1);
const pageSize = ref(5);

// 전체 페이지 수 계산
const totalPages = computed(() => {
  // 데이터가 없으면 0 반환
  if (!resourceBoardList.value || resourceBoardList.value.length === 0) {
    return 0;
  }
  return Math.ceil(resourceBoardList.value.length / pageSize.value);
});

// 현재 페이지 데이터 슬라이싱

const displayedResourceBoards = computed(() => {
  if (!resourceBoardList.value || resourceBoardList.value.length === 0) {
    return []; // 데이터가 없을 때 빈 배열 반환
  }
  const start = (currentPage.value - 1) * pageSize.value;
  const end = start + pageSize.value;
  return resourceBoardList.value.slice(start, end);
});

// 페이지 번호 목록 계산
const pages = computed(() => {
  const maxPagesToShow = 5;
  const half = Math.floor(maxPagesToShow / 2);
  let start = Math.max(1, currentPage.value - half);
  let end = Math.min(totalPages.value, currentPage.value + half);

  if (currentPage.value <= half) {
    end = Math.min(totalPages.value, maxPagesToShow);
  } else if (currentPage.value + half >= totalPages.value) {
    start = Math.max(1, totalPages.value - maxPagesToShow + 1);
  }

  return Array.from({ length: end - start + 1 }, (_, i) => start + i);
});

// 데이터 가져오기
const getResourceBoards = async () => {
  const param = searchTitle.value.trim() || ""; // 공백 제거
  try {
    const res = await resourceBoardAPI.getResourceBoards({ title: param });
    resourceBoardList.value = res.data.data; // 전체 데이터 저장
  } catch (error) {
    console.error("Failed to fetch resource boards:", error);
  }
};

// 상세 페이지로 이동
const goDetail = (data: ResourceBoards) => {
  router.push({ name: "resourceBoardDetail", params: { seq: data.자료순번 } });
};

// 페이지 이동 함수
const nextPage = () => {
  if (currentPage.value < totalPages.value) {
    currentPage.value++;
  }
};

const prevPage = () => {
  if (currentPage.value > 1) {
    currentPage.value--;
  }
};

const goToPage = (page: number) => {
  currentPage.value = page;
};

// 초기 데이터 로드
onMounted(() => {
  getResourceBoards();
});
</script>

<style scoped>
.page {
  width: 100%;
  height: 100%;
}
.page_header {
  border-bottom: 1px solid #e0e0e0;
  padding: 40px 0px;
  font-family: "Noto Sans CJK KR-Bold";
  font-size: 38px;
  display: flex;
  gap: 16px;
  align-items: center;
  line-height: 51px;
  justify-content: center;
}
.page_header_circle {
  width: 18px;
  height: 18px;
  border-radius: 18px;
  border: 5px solid #e5e8ec;
}
.page_contents {
  padding: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.contents_div {
  width: 1010px;
}
.styled-table {
  width: 100%;
  border-collapse: collapse;
  font-family: "Noto Sans KR", sans-serif;
  border-top: 3px solid #041e42;
}
.styled-table th {
  padding: 10px;
  text-align: center;
  font-size: 14px;

  border-bottom: 1px solid #dbdbdb;
  font-family: "Noto Sans CJK KR-Bold";
}
.styled-table td {
  padding: 10px;
  text-align: center;
  font-size: 14px;
  border-bottom: 1px solid #dbdbdb;
  font-family: "Noto Sans CJK KR-Light";
  color: #9b9b9b;
}
.styled-table thead tr {
  color: #272727;
  text-align: center;
  font-weight: bold;
  font-size: 14px;
  font-family: "Noto Sans CJK KR-Bold";
  height: 60px;
}

.styled-table tbody tr:hover {
  background-color: #f6f7fb;
}

.styled-table tbody tr {
  height: 60px;
  cursor: pointer;
}
.styled-table td:nth-child(1) {
  font-family: "Noto Sans CJK KR-Bold";
  font-size: 16px;
  width: 130px;
  text-align: center;
}
.styled-table td:nth-child(2) {
  color: #272727;
  font-family: "Noto Sans CJK KR-Bold";
  font-size: 16px;
  width: 500px;
  text-align: left;
}
.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 20px;
  gap: 5px;
}

.pagination_left_arrow {
  margin-right: 30px;
  cursor: pointer;
}
.pagination_right_arrow {
  margin-left: 30px;
  cursor: pointer;
}
.pagination button {
  font-weight: bold;
  width: 26px;
  height: 26px;
  border-radius: 26px;
  border: none;
  background: transparent;
  cursor: pointer;
}
.pagination button.active {
  font-weight: bold;
  background-color: black;
  color: white;
  width: 26px;
  height: 26px;
  border-radius: 26px;
}
.search_div {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 20px;
  gap: 8px;
}
.search_div select {
  width: 113px;
  height: 39px;
  padding-left: 12px;
  font-family: "Noto Sans CJK KR-Bold";
  font-size: 14px;
  border-radius: 4px;
  border: 1px solid #d0d0d0;
  color: #272727;
}
.search_div input {
  border: 1px solid #d0d0d0;
  border-radius: 4px;
  height: 39px;
  color: #272727;
  font-size: 14px;
  padding-left: 13px;
  width: 347px;
}
.search_wrap {
  position: relative;
}
.icon_search {
  position: absolute;
  width: 24px;
  height: 24px;
  top: calc(50% - 12px);
  right: calc(50% - 163px);
  cursor: pointer;
}
</style>
